import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { useNavigate } from "react-router-dom";

import { usePageTitle } from "@/hooks/usePageTitle";
import {
  ADMIN_USERS_PAGE_SIZE,
  adminUserSortColumnLabel,
  adminUserSortToApi,
  adminUsersPageCount,
  adminUsersResultRangeLabel,
  type AdminUserSortDir,
  type AdminUserSortKey,
} from "@/lib/api/admin-portal-user-list";
import {
  EMPTY_PORTAL_USER_SUMMARY,
  formatPortalUserDate,
  listAdminPortalUsers,
  updateAdminPortalUserAccessRole,
  type AdminPortalUser,
  type AdminPortalUserSummary,
  type PortalUserStatus,
} from "@/lib/api/admin-portal-users";
import type { AccessRole } from "@/lib/api/portal-access-role";
import { AdminShell } from "@/pages/admin/AdminShell";
import {
  AdminTypePill,
  PortalUserStatusBadge,
} from "@/pages/admin/AdminUserBadges";
import {
  PortalUserDetailsDrawer,
  type PortalUserDrawerStep,
} from "@/pages/admin/PortalUserDetailsDrawer";

const DRAWER_CLOSE_MS = 220;

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "refreshing" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

const SORTABLE_COLUMNS: AdminUserSortKey[] = [
  "applicant",
  "business",
  "type",
  "status",
  "requests",
  "registered",
];

export default function AdminUsersPage() {
  usePageTitle("Portal users — API Admin Panel");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | PortalUserStatus>("");
  const [typeFilter, setTypeFilter] = useState<"all" | AccessRole>("all");
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<AdminUserSortKey>("registered");
  const [sortDir, setSortDir] = useState<AdminUserSortDir>("desc");
  const [users, setUsers] = useState<AdminPortalUser[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<AdminPortalUserSummary>(
    EMPTY_PORTAL_USER_SUMMARY,
  );
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedUser, setSelectedUser] = useState<AdminPortalUser | null>(
    null,
  );
  const [drawerStep, setDrawerStep] = useState<PortalUserDrawerStep>("details");
  const [drawerEpoch, setDrawerEpoch] = useState(0);
  const [drawerClosing, setDrawerClosing] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const appliedSearchRef = useRef(debouncedSearch);
  const drawerCloseTimeoutRef = useRef<number | null>(null);
  const drawerFocusRowIdRef = useRef<string | null>(null);

  useEffect(() => {
    appliedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  function openUserRequests(email: string) {
    navigate(`/admin/control-panel?q=${encodeURIComponent(email)}`);
  }

  const clearDrawerCloseTimeout = useCallback(() => {
    if (drawerCloseTimeoutRef.current !== null) {
      window.clearTimeout(drawerCloseTimeoutRef.current);
      drawerCloseTimeoutRef.current = null;
    }
  }, []);

  const commitClose = useCallback(() => {
    clearDrawerCloseTimeout();
    setSelectedUser(null);
    setDrawerClosing(false);
    setUpdateError(null);
    setDrawerStep("details");
  }, [clearDrawerCloseTimeout]);

  const requestClose = useCallback(() => {
    if (!selectedUser || drawerClosing || savingUserId) {
      return;
    }
    if (prefersReducedMotion()) {
      commitClose();
      return;
    }
    setDrawerClosing(true);
    drawerCloseTimeoutRef.current = window.setTimeout(() => {
      drawerCloseTimeoutRef.current = null;
      commitClose();
    }, DRAWER_CLOSE_MS);
  }, [commitClose, drawerClosing, savingUserId, selectedUser]);

  function openUserDrawer(user: AdminPortalUser, step: PortalUserDrawerStep) {
    clearDrawerCloseTimeout();
    setDrawerClosing(false);
    setUpdateError(null);
    setDrawerStep(step);
    setDrawerEpoch((value) => value + 1);
    setSelectedUser(user);
  }

  function toggleUserDrawer(user: AdminPortalUser) {
    if (selectedUser?.id === user.id) {
      requestClose();
      return;
    }
    openUserDrawer(user, "details");
  }

  function openTypeEditor(
    event: MouseEvent<HTMLButtonElement>,
    user: AdminPortalUser,
  ) {
    event.stopPropagation();
    openUserDrawer(user, "edit");
  }

  async function saveAccessRole(accessRole: AccessRole): Promise<boolean> {
    const user = selectedUser;
    if (!user || user.access_role === accessRole) {
      return false;
    }
    setUpdateError(null);
    setSavingUserId(user.id);
    try {
      const updated = await updateAdminPortalUserAccessRole(user.id, accessRole);
      setUsers((current) =>
        current.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
      );
      setSelectedUser((current) =>
        current && current.id === updated.id
          ? { ...current, ...updated }
          : current,
      );
      return true;
    } catch {
      setUpdateError("Could not update account type. Try again.");
      return false;
    } finally {
      setSavingUserId(null);
    }
  }

  useEffect(() => {
    return () => {
      clearDrawerCloseTimeout();
    };
  }, [clearDrawerCloseTimeout]);

  useEffect(() => {
    if (selectedUser !== null) {
      drawerFocusRowIdRef.current = selectedUser.id;
      return;
    }
    const focusRowId = drawerFocusRowIdRef.current;
    if (!focusRowId) {
      return;
    }
    drawerFocusRowIdRef.current = null;
    const row = document.querySelector<HTMLElement>(
      `[data-testid="admin-user-row-${focusRowId}"]`,
    );
    row?.focus();
  }, [selectedUser]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = search.trim();
      if (next === appliedSearchRef.current) {
        return;
      }
      setDebouncedSearch(next);
      setPage(1);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      await Promise.resolve();
      if (cancelled) {
        return;
      }

      setLoadState((prev) =>
        prev.kind === "ready" || prev.kind === "refreshing"
          ? { kind: "refreshing" }
          : { kind: "loading" },
      );

      try {
        const response = await listAdminPortalUsers({
          q: debouncedSearch || undefined,
          status: statusFilter || undefined,
          access_role: typeFilter === "all" ? undefined : typeFilter,
          page,
          limit: ADMIN_USERS_PAGE_SIZE,
          sort: adminUserSortToApi(sortKey),
          order: sortDir,
        });
        if (cancelled) {
          return;
        }
        setUsers(response.users);
        setTotal(response.total);
        setSummary(response.summary);
        setLoadState({ kind: "ready" });
      } catch {
        if (cancelled) {
          return;
        }
        setUsers([]);
        setTotal(0);
        setSummary(EMPTY_PORTAL_USER_SUMMARY);
        setLoadState({
          kind: "error",
          message: "Could not load portal users. Try again.",
        });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, statusFilter, typeFilter, page, reloadKey, sortKey, sortDir]);

  const pageCount = useMemo(
    () => adminUsersPageCount(total, ADMIN_USERS_PAGE_SIZE),
    [total],
  );
  const safePage = Math.min(Math.max(1, page), pageCount);
  const listBusy = loadState.kind === "loading" || loadState.kind === "refreshing";
  const showRows = (loadState.kind === "ready" || loadState.kind === "refreshing") && users.length > 0;
  const hasActiveFilters =
    debouncedSearch.length > 0 || statusFilter !== "" || typeFilter !== "all";

  function selectStatusFilter(next: "" | PortalUserStatus) {
    setStatusFilter(next);
    setPage(1);
  }

  function selectTypeFilter(next: "all" | AccessRole) {
    setTypeFilter(next);
    setPage(1);
  }

  function clearFilters() {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter("");
    setTypeFilter("all");
    setPage(1);
  }

  function toggleSort(key: AdminUserSortKey) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      setPage(1);
      return;
    }
    setSortKey(key);
    setSortDir(key === "registered" ? "desc" : "asc");
    setPage(1);
  }

  return (
    <AdminShell
      activeNav="users"
      title="API portal users"
      description="Start with registered applicants. Open a row to inspect the account and correct Customer or Partner in the side panel."
      testId="admin-users-page"
      afterMain={
        selectedUser ? (
          <PortalUserDetailsDrawer
            key={`${selectedUser.id}-${drawerEpoch}`}
            user={selectedUser}
            closing={drawerClosing}
            initialStep={drawerStep}
            submitting={savingUserId === selectedUser.id}
            error={updateError}
            onClose={requestClose}
            onSave={saveAccessRole}
            onViewRequests={(user) => {
              commitClose();
              openUserRequests(user.email);
            }}
          />
        ) : null
      }
    >
      <div
        className="sum-row"
        role="group"
        aria-label="Filter by user status"
        data-testid="admin-users-summary"
      >
        <button
          type="button"
          className={`sum-card${statusFilter === "" ? " on" : ""}`}
          aria-pressed={statusFilter === ""}
          data-testid="admin-users-sum-all"
          onClick={() => selectStatusFilter("")}
        >
          <div className="sum-lbl">Total</div>
          <div className="sum-val" data-testid="admin-users-count-total">
            {summary.total}
          </div>
          <div className="sum-sub">across all statuses</div>
        </button>
        <button
          type="button"
          className={`sum-card${statusFilter === "pending" ? " on" : ""}`}
          aria-pressed={statusFilter === "pending"}
          data-testid="admin-users-sum-pending"
          onClick={() => selectStatusFilter("pending")}
        >
          <div className="sum-lbl">Pending</div>
          <div
            className="sum-val sum-val--review"
            data-testid="admin-users-count-pending"
          >
            {summary.pending}
          </div>
          <div className="sum-sub">awaiting verification</div>
        </button>
        <button
          type="button"
          className={`sum-card${statusFilter === "active" ? " on" : ""}`}
          aria-pressed={statusFilter === "active"}
          data-testid="admin-users-sum-active"
          onClick={() => selectStatusFilter("active")}
        >
          <div className="sum-lbl">Active</div>
          <div
            className="sum-val sum-val--active"
            data-testid="admin-users-count-active"
          >
            {summary.active}
          </div>
          <div className="sum-sub">can sign in</div>
        </button>
        <button
          type="button"
          className={`sum-card${statusFilter === "verified" ? " on" : ""}`}
          aria-pressed={statusFilter === "verified"}
          data-testid="admin-users-sum-verified"
          onClick={() => selectStatusFilter("verified")}
        >
          <div className="sum-lbl">Verified</div>
          <div
            className="sum-val sum-val--verified"
            data-testid="admin-users-count-verified"
          >
            {summary.verified}
          </div>
          <div className="sum-sub">email confirmed</div>
        </button>
      </div>

      <div
        className="mobile-status"
        role="group"
        aria-label="Filter by status"
        data-testid="admin-users-mobile-status"
      >
        <button
          type="button"
          className={`mobile-status-btn${statusFilter === "" ? " on" : ""}`}
          aria-pressed={statusFilter === ""}
          data-testid="admin-users-mobile-filter-all"
          onClick={() => selectStatusFilter("")}
        >
          All <span>{summary.total}</span>
        </button>
        <button
          type="button"
          className={`mobile-status-btn${statusFilter === "pending" ? " on" : ""}`}
          aria-pressed={statusFilter === "pending"}
          data-testid="admin-users-mobile-filter-pending"
          onClick={() => selectStatusFilter("pending")}
        >
          Pending <span>{summary.pending}</span>
        </button>
        <button
          type="button"
          className={`mobile-status-btn${statusFilter === "active" ? " on" : ""}`}
          aria-pressed={statusFilter === "active"}
          data-testid="admin-users-mobile-filter-active"
          onClick={() => selectStatusFilter("active")}
        >
          Active <span>{summary.active}</span>
        </button>
        <button
          type="button"
          className={`mobile-status-btn${statusFilter === "verified" ? " on" : ""}`}
          aria-pressed={statusFilter === "verified"}
          data-testid="admin-users-mobile-filter-verified"
          onClick={() => selectStatusFilter("verified")}
        >
          Verified <span>{summary.verified}</span>
        </button>
      </div>

      <div className="toolbar" data-testid="admin-users-toolbar">
        <div className="search-wrap">
          <i className="ti ti-search" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, email or company…"
            aria-label="Search portal users"
            data-testid="admin-users-search"
          />
        </div>
        <button
          type="button"
          className={`filter-btn${typeFilter === "all" ? " on" : ""}`}
          aria-pressed={typeFilter === "all"}
          data-testid="admin-users-type-all"
          onClick={() => selectTypeFilter("all")}
        >
          All types
        </button>
        <button
          type="button"
          className={`filter-btn${typeFilter === "customer" ? " on" : ""}`}
          aria-pressed={typeFilter === "customer"}
          data-testid="admin-users-type-customer"
          onClick={() => selectTypeFilter("customer")}
        >
          <i className="ti ti-building" aria-hidden="true" /> Customer
        </button>
        <button
          type="button"
          className={`filter-btn${typeFilter === "partner" ? " on" : ""}`}
          aria-pressed={typeFilter === "partner"}
          data-testid="admin-users-type-partner"
          onClick={() => selectTypeFilter("partner")}
        >
          <i className="ti ti-building-skyscraper" aria-hidden="true" />{" "}
          Partner
        </button>
        <button
          type="button"
          className={`filter-btn${loadState.kind === "refreshing" ? " is-busy" : ""}`}
          data-testid="admin-users-refresh"
          aria-label="Refresh portal users"
          disabled={listBusy}
          onClick={() => setReloadKey((key) => key + 1)}
        >
          <i
            className={`ti ${listBusy ? "ti-loader-2" : "ti-refresh"}`}
            aria-hidden="true"
          />
          {loadState.kind === "refreshing" ? "Updating" : "Refresh"}
        </button>
      </div>

      <div className="results-meta" data-testid="admin-users-results-meta">
        <span>
          {adminUsersResultRangeLabel(safePage, ADMIN_USERS_PAGE_SIZE, total)}
        </span>
        {hasActiveFilters ? (
          <button
            type="button"
            className="results-clear"
            data-testid="admin-users-clear-filters"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        ) : null}
      </div>

      <div className="tbl-wrap" data-testid="admin-users-table-wrap">
        <div
          className={`tbl-scroll${loadState.kind === "refreshing" ? " is-refreshing" : ""}`}
          data-testid="admin-users-list"
          role="region"
          tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex -- overflow lists must take keyboard focus
          aria-label="Portal users list"
          aria-busy={listBusy}
        >
          {loadState.kind === "refreshing" ? (
            <div
              className="tbl-refresh"
              data-testid="admin-users-refreshing"
              role="status"
              aria-live="polite"
            >
              <i className="ti ti-loader-2" aria-hidden="true" />
              <span>Updating results…</span>
            </div>
          ) : null}

          {loadState.kind === "loading" ? (
            <div className="empty-state" data-testid="admin-users-loading">
              <i className="ti ti-loader-2" aria-hidden="true" />
              <p>Loading users…</p>
            </div>
          ) : null}

          {loadState.kind === "error" ? (
            <div className="empty-state" data-testid="admin-users-error">
              <i className="ti ti-alert-circle" aria-hidden="true" />
              <p>{loadState.message}</p>
              <button
                type="button"
                className="filter-btn on"
                onClick={() => setReloadKey((value) => value + 1)}
              >
                Retry
              </button>
            </div>
          ) : null}

          {loadState.kind === "ready" && users.length === 0 ? (
            <div className="empty-state" data-testid="admin-users-empty">
              <i className="ti ti-users" aria-hidden="true" />
              <p>No portal users match the current filters.</p>
            </div>
          ) : null}

          {showRows ? (
            <table className="req-table users-table">
              <thead>
                <tr>
                  {SORTABLE_COLUMNS.map((key) => {
                    const active = sortKey === key;
                    return (
                      <th
                        key={key}
                        scope="col"
                        data-testid={`admin-users-sort-${key}-header`}
                        aria-sort={
                          active
                            ? sortDir === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                      >
                        <button
                          type="button"
                          className={`th-sort${active ? " on" : ""}`}
                          data-testid={`admin-users-sort-${key}`}
                          onClick={() => toggleSort(key)}
                        >
                          {adminUserSortColumnLabel(key)}
                          <i
                            className={`ti ${
                              active
                                ? sortDir === "asc"
                                  ? "ti-arrow-up"
                                  : "ti-arrow-down"
                                : "ti-selector"
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const hasRequests = user.access_request_count > 0;
                  return (
                    <tr
                      key={user.id}
                      className={`req-tr req-tr--interactive${
                        selectedUser?.id === user.id ? " on" : ""
                      }`}
                      data-testid={`admin-user-row-${user.id}`}
                      tabIndex={0}
                      aria-label={`View details for ${user.email}`}
                      onClick={() => toggleUserDrawer(user)}
                      onKeyDown={(event: KeyboardEvent<HTMLTableRowElement>) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleUserDrawer(user);
                        }
                      }}
                    >
                      <td>
                        <div
                          className="req-name"
                          data-testid="admin-user-contact-name"
                        >
                          {user.contact_name}
                        </div>
                        <div
                          className="req-email"
                          data-testid="admin-user-email"
                        >
                          {user.email}
                        </div>
                      </td>
                      <td>
                        <div className="req-biz">{user.company_name}</div>
                      </td>
                      <td>
                        <div className="type-cell">
                          <AdminTypePill type={user.access_role} />
                          <button
                            type="button"
                            className="type-edit-btn"
                            aria-label={`Edit account type for ${user.contact_name}`}
                            data-testid={`admin-user-edit-type-${user.id}`}
                            onClick={(event) => openTypeEditor(event, user)}
                            onKeyDown={(event) => event.stopPropagation()}
                          >
                            <i className="ti ti-pencil" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                      <td>
                        <PortalUserStatusBadge status={user.status} />
                      </td>
                      <td>
                        <div
                          className={`req-count${
                            hasRequests ? " req-count--link" : ""
                          }`}
                          data-testid="admin-user-request-count"
                        >
                          {user.access_request_count}
                        </div>
                      </td>
                      <td>
                        <div className="req-date">
                          {formatPortalUserDate(user.created_at)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>

        {total > ADMIN_USERS_PAGE_SIZE ? (
          <div className="pager" data-testid="admin-users-pager">
            <button
              type="button"
              className="filter-btn"
              disabled={safePage <= 1 || listBusy}
              onClick={() => {
                if (selectedUser) {
                  requestClose();
                }
                setPage((value) => Math.max(1, value - 1));
              }}
              data-testid="admin-users-prev-page"
            >
              Previous
            </button>
            <span data-testid="admin-users-page-label">
              Page {safePage} of {pageCount}
            </span>
            <button
              type="button"
              className="filter-btn"
              disabled={safePage >= pageCount || listBusy}
              onClick={() => {
                if (selectedUser) {
                  requestClose();
                }
                setPage((value) => Math.min(pageCount, value + 1));
              }}
              data-testid="admin-users-next-page"
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </AdminShell>
  );
}
